import type { KeyState } from '../types/game';

/**
 * Keyboard input manager class
 */
export class KeyboardManager {
  private keyState: KeyState = {};
  private listeners: Array<() => void> = [];

  constructor() {
    this.handleKeyDown = this.handleKeyDown.bind(this);
    this.handleKeyUp = this.handleKeyUp.bind(this);
  }

  private handleKeyDown(event: KeyboardEvent): void {
    this.keyState = {
      ...this.keyState,
      [event.key]: true
    };
    this.notifyListeners();
  }

  private handleKeyUp(event: KeyboardEvent): void {
    const newState = { ...this.keyState };
    delete newState[event.key];
    this.keyState = newState;
    this.notifyListeners();
  }

  private notifyListeners(): void {
    this.listeners.forEach(listener => listener());
  }

  /**
   * Start listening for keyboard events
   */
  public startListening(): void {
    document.addEventListener('keydown', this.handleKeyDown);
    document.addEventListener('keyup', this.handleKeyUp);
  }

  /**
   * Stop listening for keyboard events
   */
  public stopListening(): void {
    document.removeEventListener('keydown', this.handleKeyDown);
    document.removeEventListener('keyup', this.handleKeyUp);
  }

  /**
   * Add a listener for key state changes
   */
  public addListener(listener: () => void): void {
    this.listeners.push(listener);
  }

  /**
   * Remove a listener
   */
  public removeListener(listener: () => void): void {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  /**
   * Check if a specific key is currently pressed
   */
  public isKeyPressed(key: string): boolean {
    return Boolean(this.keyState[key]);
  }

  /**
   * Check if multiple keys are pressed
   */
  public areKeysPressed(keys: string[]): boolean {
    return keys.every(key => this.keyState[key]);
  }

  /**
   * Get all currently pressed keys
   */
  public getPressedKeys(): string[] {
    return Object.keys(this.keyState);
  }

  /**
   * Get current key state
   */
  public getKeyState(): KeyState {
    return { ...this.keyState };
  }
}

/**
 * Hook-like interface for keyboard input (to be used with React hooks when available)
 */
export function createKeyboardHook() {
  return {
    // This will be implemented when React hooks are available
    useKeyboard: () => ({
      keyState: {} as KeyState,
      isKeyPressed: (_key: string) => false,
      areKeysPressed: (_keys: string[]) => false,
      getPressedKeys: () => [] as string[]
    })
  };
} 